const PropTypes = require('prop-types');
// @vendor
import React, { useEffect } from 'react';

// @utilities
const PersonalDataHelper = require('utilities/personalDataHelper');
const { wizardEventNavigation, editEmailSignatureKeypressLink, tealiumEmailWizard } = require('utilities/taggingInfo/profileEditorViewTagging');

// @commons
const { formatText } = require('core/i18n').i18n;
const Wizard = require('components/commons/wizard/wizard');
const WizardStep = require('components/commons/wizardStep/wizardStep');

// @constants
import { VERIFY_OTP_MAX_ATTEMPTS_CODE } from 'constants/index';
import { PROFILE_EDIT_FORMS } from 'constants/myProfileEditor'

// @components
const PreLeaveModalComponent = require('components/preLeaveModalComponent/preLeaveModalComponent');
import EditEmailOtpStep from './EditEmailOtpStep';
const EditFormStep = require('./EditFormStep');
const ConfirmStep = require('./ConfirmStep');

const MyProfileEditorWizard = ({         
    actionChangeValue,
    beforeLeaving,
    deleteAdditionalCountries,
    immProfileEditor,
    immProfileFields,
    isReady,
    onCancel,
    onConfirmLeave,
    onFinish,
    setAddingTaxAdditionalCountry,
    setEditingTaxAdditionalCountry,
    setVisibleStep,
    setWillCancel,
    updateAdditionalCountries,
    processType,
    validateStep,
    onHandleNextDisableButton,
    disableNextButton,
    onHandleSetOtp,
    onHandleNextClick,
    onHandleVerifyOtpEmail,
    otp,
    setEmailUuid,
    history,
    initProfileOtpEmail,
    resendOtpEmail,
}) => {

    let immSteps = immProfileEditor.get('steps');
    const { isRequestOtp, verifyOtpErrorStatus, requestOtpErrorStatus } = immProfileEditor.toJS();
    const VISIBLE_EMAIL_STEP = 1;

    useEffect(() => {
        if(immProfileEditor.get('editMode') === PROFILE_EDIT_FORMS.EMAIL) {
            setEmailUuid();
        }
    },[]);

    function validateEmailEditStep() {
        const immStep = immProfileEditor.getIn(['steps', 0]);
        const visibleStep = immProfileEditor.get('visibleStep') || VISIBLE_EMAIL_STEP;

        if(visibleStep === VISIBLE_EMAIL_STEP) {
            if( immStep.get('emailDirty') && immStep.get('repeatEmailDirty') &&
            // we check that it has the right format
            PersonalDataHelper.isValidEmail(immStep.get('email')) &&
            // we check that new email and repeated new email are the same
            immStep.get('email') === immStep.get('repeatEmail') ) {
                initProfileOtpEmail(false);
            } else {
                validateStep(2, history)
            }
        } else if(visibleStep === 2) {
            onHandleVerifyOtpEmail();
        }
    }
    
    const actions = {
        validateStep: immProfileEditor.get('editMode') === PROFILE_EDIT_FORMS.EMAIL ? validateEmailEditStep : validateStep,
        setVisibleStep,
        setWillCancel: (immProfileEditor.get('inProgress') ? setWillCancel : onConfirmLeave),
        finish: onFinish,
        cancel: onCancel
    };

    const buildSteps = () => {
        const wizardSteps = [
            <WizardStep
                position="first"
                cancelButton={true}
                nextButtonDisabled={disableNextButton}
                onNextClick={immProfileEditor.get('editMode') === PROFILE_EDIT_FORMS.EMAIL ? onHandleNextClick : null}
                isLoading={immProfileEditor.get('isOtpEmailFetching')}
            >
                <EditFormStep
                    actionChangeValue={actionChangeValue}
                    deleteAdditionalCountries={deleteAdditionalCountries}
                    immProfileFields={immProfileFields}
                    immProfileEditor={immProfileEditor}
                    updateAdditionalCountries={updateAdditionalCountries}
                    setAddingTaxAdditionalCountry={setAddingTaxAdditionalCountry}
                    setEditingTaxAdditionalCountry={setEditingTaxAdditionalCountry}
                    onHandleNextDisableButton={onHandleNextDisableButton}
                />
            </WizardStep>
        ];

        immProfileEditor.get('editMode') === PROFILE_EDIT_FORMS.EMAIL && wizardSteps.push(
            <WizardStep
                    position="middle"
                    cancelButton={true}
                    nextButton={true}
                    previousButton={true}
                    nextButtonDisabled={otp.length < 1 || verifyOtpErrorStatus === VERIFY_OTP_MAX_ATTEMPTS_CODE || requestOtpErrorStatus === VERIFY_OTP_MAX_ATTEMPTS_CODE }
                    isLoading={isRequestOtp}
                >
                    <EditEmailOtpStep 
                        immProfileEditor={immProfileEditor}
                        resendOtpEmail={resendOtpEmail}
                        onHandleSetOtp={onHandleSetOtp}
                        setEmailUuid={setEmailUuid}
                    />
                </WizardStep>
            )
        
        wizardSteps.push(
            <WizardStep
                addLoadingBehaviour={true}
                position="last"
                cancelButton={true}
                immFinalButton="profile-modifyButton"
                disablePrevious={immProfileEditor.get('editMode') === PROFILE_EDIT_FORMS.EMAIL}
            >
                <ConfirmStep
                    immProfileFields={immProfileFields}
                    immProfileEditor={immProfileEditor}
                    taggingOnKeyPress={immProfileEditor.get('editMode') === PROFILE_EDIT_FORMS.EMAIL && editEmailSignatureKeypressLink}
                />
            </WizardStep>
        )

        return wizardSteps;
    }

    return (
        <div>
            <PreLeaveModalComponent
                confirmationMessage={formatText('wizard-redirectConfirmationMessage')}
                confirmationTitle={formatText('wizard-redirectConfirmationTitle')}
                acceptText={formatText('wizard-redirectConfirmationAcceptButton')}
                cancelText={formatText('wizard-redirectConfirmationCancelButton')}
                isReady={isReady}
                beforeLeaving={beforeLeaving}
                onConfirmLeave={onConfirmLeave}
            />
            <Wizard
                actions={actions}
                immSteps={immSteps}
                visibleStep={immProfileEditor.get('visibleStep')}
                stepsAmount={immSteps.size}
                processType={processType}
                tealiumEvents={immProfileEditor.get('editMode') === PROFILE_EDIT_FORMS.EMAIL && wizardEventNavigation()}
                tealium={immProfileEditor.get('editMode') === PROFILE_EDIT_FORMS.EMAIL && tealiumEmailWizard[immProfileEditor.get('visibleStep') - 1]}
                isTaggingSecondVersion={true}
            >
                { buildSteps() }
            </Wizard>
        </div>
    );
};

MyProfileEditorWizard.propTypes = {
    actionChangeValue: PropTypes.func.isRequired,
    beforeLeaving: PropTypes.func.isRequired,
    deleteAdditionalCountries: PropTypes.func.isRequired,
    initProfileOtpEmail: PropTypes.func,
    immProfileEditor: PropTypes.object.isRequired,
    immProfileFields: PropTypes.object.isRequired,
    isReady: PropTypes.func.isRequired,
    processType: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onConfirmLeave: PropTypes.func.isRequired,
    onFinish: PropTypes.func.isRequired,
    resendOtpEmail: PropTypes.func,
    setVisibleStep: PropTypes.func.isRequired,
    setWillCancel: PropTypes.func.isRequired,
    setAddingTaxAdditionalCountry: PropTypes.func.isRequired,
    setEditingTaxAdditionalCountry: PropTypes.func.isRequired,
    updateAdditionalCountries: PropTypes.func.isRequired,
    validateStep: PropTypes.func.isRequired,
    onHandleNextClick: PropTypes.func,
    onHandleNextDisableButton: PropTypes.func,
    disableNextButton: PropTypes.bool,
};

MyProfileEditorWizard.defaultProps = {
    onHandleNextClick: () => {},
    processType: null,
};

module.exports = MyProfileEditorWizard;